// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Trash2, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRevokePasskeyMutation } from "@/api/hooks/use-passkeys";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PasskeyCredential } from "@/lib/auth/types";

interface RevokePasskeyDialogProps {
  credential: PasskeyCredential | null;
  onClose: () => void;
}

export function RevokePasskeyDialog({ credential, onClose }: RevokePasskeyDialogProps) {
  const { t } = useTranslation("back-office");
  const revoke = useRevokePasskeyMutation();

  // Keep the last credential around so the content doesn't blank out during the close animation.
  const lastCredential = useRef(credential);
  if (credential) lastCredential.current = credential;
  const display = credential ?? lastCredential.current;
  const displayName = display?.friendlyName || t("passkeys.title");

  async function onSubmit() {
    if (!display) return;
    try {
      await revoke.mutateAsync(display.id);
      toast.success(t("passkeys.revoked"));
      onClose();
    } catch {
      toast.error(t("passkeys.error"));
    }
  }

  return (
    <Dialog open={!!credential} onOpenChange={() => onClose()}>
      <DialogContent id="dialog-revoke-passkey" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane variant="destructive" icon={Trash2} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("passkeys.revokeDialog.title")}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {t("passkeys.revokeDialog.prompt")}{" "}
                    <span className="font-semibold text-foreground">{displayName}</span>?
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-sm">{t("passkeys.revokeDialog.warning")}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="destructive" onClick={onSubmit} disabled={revoke.isPending}>
            {revoke.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t("passkeys.revokeDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
